// Лист раздела «Правила»: одна панель поверх карты вместо трёх колонок.
//
// Карта во всю ширину, лист плавает слева, как панели «Буёв» и «Квот»; на узком
// экране он становится нижней шторкой. Лист ПЕРЕТАСКИВАЕТСЯ за верхнюю строку
// (`map/floatingPanel.ts`, общий с «Квотами»): высокий лист закрывает треть
// карты, и место под ним иначе не посмотреть, не свернув список.
//
// Над списком — сводка вердиктов: полоса долей и счётчики групп. Это ответ на
// вопрос «что здесь действует» до чтения списка; счётчик ведёт к своей группе.
// Свёрнутый лист — та же сводка столбиком.
//
// Свёрнутость — удобство одного человека, поэтому `localStorage` под
// try/catch, а не адрес: ссылка, которой делятся, не должна сворачивать лист
// получателю.

import { useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { useT } from '../i18n'
import type { RuleNorm } from '../api'
import { GROUP_FILL } from './RuleGroups'
import { verdictLedger } from './rulesGroups'
import type { RuleGroup } from './rulesGroups'
import type { useFloatingPanel } from '../map/floatingPanel'

const KEY = 'rules.sheet.collapsed'

function readCollapsed(): boolean {
  try { return localStorage.getItem(KEY) === '1' } catch { return false }
}

function writeCollapsed(v: boolean): void {
  try { localStorage.setItem(KEY, v ? '1' : '0') } catch { /* без хранилища — без памяти */ }
}

export function useSheetCollapsed(): [boolean, (v: boolean) => void] {
  const [c, setC] = useState(readCollapsed)
  return [c, v => { setC(v); writeCollapsed(v) }]
}

const FOCUS = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-sonar'

/** Шесть точек ручки: тот же знак, что у панели «Квот». */
function Grip() {
  return (
    <svg aria-hidden viewBox="0 0 8 14" className="h-3.5 w-2 shrink-0 text-faint">
      {[2, 7, 12].flatMap(y => [1.5, 6.5].map(x => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.2" fill="currentColor" />))}
    </svg>
  )
}

/** Полоса долей и счётчики групп. Без касающихся норм — ничего: пустая полоса
 *  читалась бы как «ничего не загрузилось». */
function Ledger({ norms, onJump }: { norms: RuleNorm[]; onJump: (g: RuleGroup) => void }) {
  const t = useT()
  const segs = verdictLedger(norms)
  if (!segs.length) return null
  return (
    <div>
      <div aria-hidden className="flex h-1.5 gap-px overflow-hidden rounded-full bg-hull">
        {segs.map(s => (
          <div key={s.key} className={GROUP_FILL[s.key]} style={{ width: `${s.pct}%` }} />
        ))}
      </div>
      <ul className="mt-1.5 flex flex-wrap gap-x-1 gap-y-0.5">
        {segs.map(s => (
          <li key={s.key}>
            <button type="button" onClick={() => onJump(s.key)} title={t(`rules.group.${s.key}`)}
              className={`flex flex-col items-start rounded-md px-1.5 py-0.5 text-left hover:bg-haze ${FOCUS}`}>
              <span className="font-heading text-[17px] tabular-nums font-semibold leading-tight text-foam">{s.count}</span>
              <span className="flex items-center gap-1 text-[11px] leading-tight text-mist">
                <span aria-hidden className={`h-1.5 w-1.5 shrink-0 rounded-full ${GROUP_FILL[s.key]}`} />
                {t(`rules.ledger.${s.key}`)}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

type Panel = ReturnType<typeof useFloatingPanel>

export function RulesSheet({
  collapsed, onCollapse, norms, title, count, controls, children, footer, panel, movable, onJump,
}: {
  collapsed: boolean
  onCollapse: (v: boolean) => void
  norms: RuleNorm[]
  /** Название каталога — строка или выбор каталога. */
  title: ReactNode
  /** «37 из 1610» — сколько норм под фильтром. */
  count: ReactNode
  /** Поиск и чипы фильтров. */
  controls: ReactNode
  children: ReactNode
  footer: ReactNode
  panel: Panel
  /** Перетаскивать можно только на широком экране: на телефоне лист — шторка. */
  movable: boolean
  onJump: (g: RuleGroup) => void
}) {
  const t = useT()
  const frame = 'pointer-events-auto absolute z-10 flex flex-col border border-hull bg-abyss/95 shadow-console backdrop-blur'
  const style: CSSProperties | undefined = panel.style
  const handle = {
    ...panel.handleProps,
    tabIndex: movable ? 0 : undefined,
    'aria-label': movable ? t('rules.sheet.move') : undefined,
    title: movable ? t('rules.sheet.move') : undefined,
    style: movable ? { touchAction: 'none' as const } : undefined,
  }
  const cursor = movable ? (panel.dragging ? 'cursor-grabbing' : 'cursor-grab') : ''

  if (collapsed) {
    const segs = verdictLedger(norms)
    return (
      <div ref={el => { panel.panelRef.current = el }} style={style}
        className={`${frame} left-3 top-3 w-12 items-center gap-2 rounded-xl pb-2
          max-md:inset-x-0 max-md:bottom-0 max-md:left-0 max-md:top-auto max-md:w-auto max-md:flex-row max-md:rounded-b-none max-md:px-3 max-md:py-1.5
          ${panel.dragging ? 'select-none' : ''}`}>
        <div {...handle} className={`flex w-full justify-center rounded-t-xl pt-2 max-md:hidden ${FOCUS} ${cursor}`}>
          <Grip />
        </div>
        <button type="button" onClick={() => onCollapse(false)} aria-label={t('rules.sheet.open')}
          className={`rounded px-1.5 py-0.5 text-foam hover:bg-haze ${FOCUS}`}>
          <span aria-hidden className="max-md:hidden">»</span>
          <span aria-hidden className="md:hidden">⌃</span>
        </button>
        <ul className="flex flex-col items-center gap-2 max-md:flex-row">
          {segs.map(s => (
            <li key={s.key} title={t(`rules.group.${s.key}`)} className="flex flex-col items-center gap-0.5 max-md:flex-row">
              <span aria-hidden className={`h-1.5 w-1.5 rounded-full ${GROUP_FILL[s.key]}`} />
              <span className="text-[12px] font-semibold tabular-nums text-foam">{s.count}</span>
            </li>
          ))}
        </ul>
      </div>
    )
  }

  return (
    <aside ref={el => { panel.panelRef.current = el }} style={style}
      className={`${frame} left-3 top-3 max-h-[calc(100%-24px)] w-[380px] max-w-[calc(100%-24px)] overflow-hidden rounded-xl
        max-md:inset-x-0 max-md:bottom-0 max-md:left-0 max-md:top-auto max-md:h-[58vh] max-md:max-h-none max-md:w-auto max-md:max-w-none max-md:rounded-b-none
        ${panel.dragging ? 'select-none' : ''}`}>
      <div className="space-y-2.5 border-b border-hull px-3 pb-3">
        <div {...handle} className={`-mx-3 flex items-center gap-2 px-3 pt-2.5 ${FOCUS} ${cursor}`}>
          {movable && <Grip />}
          <div data-panel-text="" className="min-w-0 cursor-text select-text">{title}</div>
          <span aria-hidden className="min-w-2 flex-1 self-stretch" />
          <button type="button" onClick={() => onCollapse(true)} aria-label={t('rules.sheet.close')}
            className={`shrink-0 rounded px-1.5 py-0.5 text-mist hover:bg-haze hover:text-foam ${FOCUS}`}>
            <span aria-hidden className="max-md:hidden">«</span>
            <span aria-hidden className="md:hidden">⌄</span>
          </button>
        </div>
        <Ledger norms={norms} onJump={onJump} />
        {controls}
        <div className="text-[11px] tabular-nums text-faint">{count}</div>
      </div>
      <div data-rules-scroll className="min-h-0 flex-1 overflow-y-auto overscroll-contain">{children}</div>
      <div className="border-t border-hull px-3 py-1.5 text-[11px] text-faint">{footer}</div>
    </aside>
  )
}
